import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  PhoneOff,
  Stethoscope,
  Upload,
  Send,
  Plus,
  Trash2,
  Save,
  FileCheck,
  Paperclip,
  Printer,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import AgoraVideoPanel from "../components/AgoraVideoPanel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import DiagnosticTestsLive from "../components/DiagnosticTestsLive";

function fmtClock(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function EmptyMedicine() {
  return { name: "", dosage: "", frequency: "", duration: "", notes: "" };
}

function MedicineSearchInput({ value, onChange, testId, placeholder = "Name (e.g. Paracetamol 500mg)" }) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/medicines/search?q=${encodeURIComponent(query.trim())}&limit=25`);
        setResults(data || []);
        if (data && data.length > 0) setOpen(true);
      } catch (err) {
        console.error("Medicine search error", err);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (item) => {
    setQuery(item);
    onChange(item);
    setOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <Input
        data-testid={testId}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
        }}
        onFocus={() => {
          if (results.length > 0) setOpen(true);
        }}
        placeholder={placeholder}
      />
      {open && results.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-border rounded-xl shadow-xl divide-y divide-border text-xs">
          {results.map((item, i) => (
            <li
              key={i}
              className="p-2.5 hover:bg-slate-100 cursor-pointer font-medium text-slate-800 transition-colors flex items-center gap-1.5"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(item);
              }}
            >
              <span className="text-teal-600 font-bold">Rx</span> {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Mode: 'doctor' | 'patient'
export default function ConsultationRoom({ mode }) {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [appt, setAppt] = useState(null);
  const [consult, setConsult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [tick, setTick] = useState(0);
  const [endOpen, setEndOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const messagesEndRef = useRef(null);

  // Prescription state (doctor only)
  const [rx, setRx] = useState({
    medicines: [EmptyMedicine()],
    diagnosis: "",
    advice: "",
    tests_recommended: [],
    follow_up_date: "",
    finalized: false,
  });
  const [testInput, setTestInput] = useState("");
  const [savingRx, setSavingRx] = useState(false);
  const fileInputRef = useRef(null);

  const startedAt = consult?.started_at ? new Date(consult.started_at) : null;
  const elapsed = startedAt
    ? Math.max(0, Math.floor((Date.now() - startedAt.getTime()) / 1000) + tick * 0)
    : 0;

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const fetchInitial = useCallback(async () => {
    setLoading(true);
    try {
      if (mode === "doctor") {
        const { data: apptData } = await api.get(`/doctor/appointments/${appointmentId}`);
        setAppt(apptData);
        if (apptData.prescription) {
          setRx({
            medicines: apptData.prescription.medicines?.length
              ? apptData.prescription.medicines
              : [EmptyMedicine()],
            diagnosis: apptData.prescription.diagnosis || "",
            advice: apptData.prescription.advice || "",
            tests_recommended: apptData.prescription.tests_recommended || [],
            follow_up_date: apptData.prescription.follow_up_date || "",
            finalized: !!apptData.prescription.finalized,
          });
        }
        if (apptData.status === "completed") {
          setConsult(apptData.consultation || null);
        } else {
          // Always start (or noop) so timer is running
          const { data: started } = await api.post(`/doctor/appointments/${appointmentId}/start`);
          setConsult(started.consultation);
        }
      } else {
        const { data } = await api.get(`/patient/appointments/${appointmentId}/consultation`);
        setAppt(data.appointment);
        setConsult(data.consultation);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [appointmentId, mode]);

  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  // Poll messages and files every 3s
  useEffect(() => {
    if (!consult?.id) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/consultations/${consult.id}/messages`);
        if (!cancelled) setMessages(data.items || []);
        if (mode === "patient" || mode === "clinic") {
          const { data: re } = await api.get(`/patient/appointments/${appointmentId}/consultation`);
          if (!cancelled && re.consultation) setConsult(re.consultation);
        } else {
          const { data: re } = await api.get(`/doctor/appointments/${appointmentId}/consultation`);
          if (!cancelled && re && re.id) setConsult(re);
        }
      } catch (_e) {
        /* ignore */
      }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [consult?.id, appointmentId, mode]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || !consult?.id) return;
    setDraft("");
    try {
      const { data: msg } = await api.post(`/consultations/${consult.id}/messages`, { text });
      setMessages((m) => [...m, msg]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setDraft(text);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !consult?.id) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File too large (max 2MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const result = reader.result?.toString() || "";
      const b64 = result.split(",")[1] || "";
      try {
        await api.post(`/consultations/${consult.id}/upload`, {
          name: file.name,
          mime_type: file.type || "application/octet-stream",
          base64_data: b64,
        });
        toast.success("File shared");
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // -------- doctor prescription helpers --------
  const updateMedicine = (idx, field, val) => {
    setRx((r) => ({
      ...r,
      medicines: r.medicines.map((m, i) => (i === idx ? { ...m, [field]: val } : m)),
    }));
  };
  const addMedicine = () => setRx((r) => ({ ...r, medicines: [...r.medicines, EmptyMedicine()] }));
  const removeMedicine = (idx) =>
    setRx((r) => ({ ...r, medicines: r.medicines.filter((_, i) => i !== idx) }));
  const addTest = () => {
    const v = testInput.trim();
    if (!v) return;
    setRx((r) => ({ ...r, tests_recommended: [...new Set([...r.tests_recommended, v])] }));
    setTestInput("");
  };
  const removeTest = (t) =>
    setRx((r) => ({ ...r, tests_recommended: r.tests_recommended.filter((x) => x !== t) }));

  const savePrescription = async (finalize) => {
    setSavingRx(true);
    try {
      const payload = {
        medicines: rx.medicines.filter((m) => m.name?.trim()),
        diagnosis: rx.diagnosis || null,
        advice: rx.advice || null,
        tests_recommended: rx.tests_recommended,
        follow_up_date: rx.follow_up_date || null,
        finalized: finalize,
      };
      const { data } = await api.post(`/doctor/appointments/${appointmentId}/prescription`, payload);
      setRx((r) => ({ ...r, finalized: !!data.finalized }));
      toast.success(finalize ? "Prescription finalized" : "Draft saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingRx(false);
    }
  };

  const completeConsultation = async () => {
    setCompleting(true);
    try {
      const hasRx = rx.medicines.some((m) => m.name?.trim()) || rx.diagnosis?.trim();
      if (!hasRx) {
        toast.warning("Prescription is empty — completing without an Rx draft.");
      }
      await api.post(`/doctor/appointments/${appointmentId}/complete`);
      toast.success("Appointment marked completed");
      setEndOpen(false);
      navigate("/doctor/appointments");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCompleting(false);
    }
  };

  const sharedFiles = consult?.shared_files || [];
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";

  const openFile = (f) => {
    // include auth header by fetching as blob
    api
      .get(`/files/${f.id}`, { responseType: "blob" })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        window.open(url, "_blank", "noopener");
      })
      .catch((err) =>
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message),
      );
  };

  return (
    <div className="min-h-screen bg-card text-foreground flex flex-col">
      <Navbar />
      <main
        data-testid="consultation-room"
        className="flex-1 px-4 lg:px-8 py-4 max-w-7xl w-full mx-auto"
      >
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          {mode === "doctor" && appt?.status !== "completed" && (
            <Button
              data-testid="consult-end-btn"
              variant="outline"
              onClick={() => setEndOpen(true)}
              className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800"
            >
              <PhoneOff className="h-4 w-4 mr-1.5" /> End consultation
            </Button>
          )}
        </div>

        {loading ? (
          <div className="h-[60vh] bg-muted rounded-2xl animate-pulse" />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* LEFT: Video */}
            <AgoraVideoPanel
              consultationId={consult?.id}
              startedAt={startedAt}
              elapsed={elapsed}
              canShow={mode === "doctor" ? !!consult?.started_at : true}
              isCompleted={appt?.status === "completed"}
            />

            {/* RIGHT: Tabs */}
            <aside className="lg:col-span-2">
              <Tabs defaultValue={mode === "doctor" ? "prescription" : "chat"}>
                <TabsList className="bg-secondary p-1 rounded-full h-12 w-full">
                  <TabsTrigger
                    value="chat"
                    data-testid="consult-tab-chat"
                    className="flex-1 rounded-full data-[state=active]:bg-white"
                  >
                    Chat
                  </TabsTrigger>
                  <TabsTrigger
                    value="reports"
                    data-testid="consult-tab-reports"
                    className="flex-1 rounded-full data-[state=active]:bg-white"
                  >
                    Reports
                  </TabsTrigger>
                  <TabsTrigger
                    value="prescription"
                    data-testid="consult-tab-rx"
                    className="flex-1 rounded-full data-[state=active]:bg-white"
                  >
                    {mode === "doctor" ? "Rx" : "Prescription"}
                  </TabsTrigger>
                </TabsList>

                {/* CHAT */}
                <TabsContent value="chat" className="mt-3">
                  <div className="bg-white border border-border rounded-2xl flex flex-col h-[60vh]">
                    <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="consult-chat-list">
                      {messages.length === 0 ? (
                        <div className="text-center text-sm text-muted-foreground py-10">
                          No messages yet. Say hi 👋
                        </div>
                      ) : (
                        messages.map((m) => {
                          const mine = m.sender_id === user?.id;
                          return (
                            <div
                              key={m.id}
                              data-testid={`consult-chat-msg-${m.id}`}
                              className={"flex " + (mine ? "justify-end" : "justify-start")}
                            >
                              <div
                                className={
                                  "max-w-[80%] px-3 py-2 rounded-2xl text-sm " +
                                  (mine
                                    ? "bg-primary text-primary-foreground rounded-br-md"
                                    : "bg-secondary text-secondary-foreground rounded-bl-md")
                                }
                              >
                                {!mine && (
                                  <div className="text-[10px] uppercase tracking-wide opacity-70 mb-0.5">
                                    {m.sender_name} · {m.sender_role}
                                  </div>
                                )}
                                <div className="whitespace-pre-wrap">{m.text}</div>
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                    <form onSubmit={sendMessage} className="border-t border-border p-3 flex gap-2">
                      <Input
                        data-testid="consult-chat-input"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder="Type a message…"
                        className="rounded-full h-11"
                      />
                      <Button
                        type="submit"
                        data-testid="consult-chat-send-btn"
                        disabled={!draft.trim()}
                        className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </form>
                  </div>
                </TabsContent>

                {/* REPORTS */}
                <TabsContent value="reports" className="mt-3">
                  <div className="bg-white border border-border rounded-2xl p-4 space-y-3 h-[60vh] overflow-y-auto">
                    <DiagnosticTestsLive
                      fetchPath={
                        mode === "doctor"
                          ? `/doctor/appointments/${appointmentId}/diagnostic-tests`
                          : `/patient/appointments/${appointmentId}/diagnostic-tests`
                      }
                    />
                    <div className="flex items-center justify-between pt-2 border-t border-border">
                      <div>
                        <div className="font-semibold">Shared files</div>
                        <div className="text-xs text-muted-foreground">
                          Lab reports, scans, photos. Max 2MB each.
                        </div>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFile}
                        className="hidden"
                        data-testid="consult-file-input"
                      />
                      <Button
                        variant="outline"
                        data-testid="consult-upload-btn"
                        onClick={() => fileInputRef.current?.click()}
                        className="rounded-full"
                      >
                        <Upload className="h-4 w-4 mr-1.5" /> Upload
                      </Button>
                    </div>

                    {sharedFiles.length === 0 ? (
                      <div className="text-sm text-muted-foreground bg-secondary/60 rounded-xl p-4 text-center">
                        No files shared yet.
                      </div>
                    ) : (
                      <ul className="space-y-2">
                        {sharedFiles.map((f) => (
                          <li
                            key={f.id}
                            data-testid={`consult-file-${f.id}`}
                            className="flex items-center gap-3 border border-border rounded-xl p-3"
                          >
                            <span className="h-9 w-9 rounded-lg bg-accent text-accent-foreground flex items-center justify-center">
                              <Paperclip className="h-4 w-4" />
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{f.name}</div>
                              <div className="text-[11px] text-muted-foreground">
                                {f.uploaded_by_role} · {new Date(f.at).toLocaleString("en-IN")}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openFile(f)}
                              className="rounded-full"
                            >
                              View
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="text-[11px] text-muted-foreground italic pt-2">
                      Live diagnostic reports stream above. File uploads here are doctor/patient shared files.
                    </div>
                  </div>
                </TabsContent>

                {/* PRESCRIPTION */}
                <TabsContent value="prescription" className="mt-3">
                  {mode === "doctor" ? (
                    <div
                      data-testid="consult-rx-form"
                      className="bg-white border border-border rounded-2xl p-4 space-y-4 h-[60vh] overflow-y-auto"
                    >
                      <div>
                        <Label className="text-sm font-medium">Diagnosis</Label>
                        <Textarea
                          data-testid="rx-diagnosis"
                          value={rx.diagnosis}
                          onChange={(e) => setRx((r) => ({ ...r, diagnosis: e.target.value }))}
                          placeholder="Provisional diagnosis…"
                          className="mt-1 min-h-[60px]"
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-sm font-medium">Medicines</Label>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            data-testid="rx-add-medicine-btn"
                            onClick={addMedicine}
                            className="rounded-full"
                          >
                            <Plus className="h-3.5 w-3.5 mr-1" /> Add medicine
                          </Button>
                        </div>
                        <div className="space-y-3">
                          {rx.medicines.map((m, idx) => (
                            <div
                              key={idx}
                              data-testid={`rx-medicine-row-${idx}`}
                              className="border border-border rounded-xl p-3 space-y-2 relative"
                            >
                              <div className="grid grid-cols-2 gap-2">
                                <MedicineSearchInput
                                  testId={`rx-medicine-${idx}-name`}
                                  value={m.name || ""}
                                  onChange={(val) => updateMedicine(idx, "name", val)}
                                  placeholder="Medicine Name & Salt..."
                                />
                                <Input
                                  data-testid={`rx-medicine-${idx}-dosage`}
                                  value={m.dosage || ""}
                                  onChange={(e) => updateMedicine(idx, "dosage", e.target.value)}
                                  placeholder="Dosage"
                                />
                                <Input
                                  data-testid={`rx-medicine-${idx}-frequency`}
                                  value={m.frequency || ""}
                                  onChange={(e) => updateMedicine(idx, "frequency", e.target.value)}
                                  placeholder="Frequency (1-0-1)"
                                />
                                <Input
                                  data-testid={`rx-medicine-${idx}-duration`}
                                  value={m.duration || ""}
                                  onChange={(e) => updateMedicine(idx, "duration", e.target.value)}
                                  placeholder="Duration (5 days)"
                                />
                              </div>
                              <Input
                                value={m.notes || ""}
                                onChange={(e) => updateMedicine(idx, "notes", e.target.value)}
                                placeholder="Notes (after food, etc.)"
                              />
                              {rx.medicines.length > 1 && (
                                <button
                                  type="button"
                                  data-testid={`rx-medicine-${idx}-remove`}
                                  onClick={() => removeMedicine(idx)}
                                  className="absolute top-2 right-2 text-muted-foreground hover:text-rose-600"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-medium">Tests recommended</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            data-testid="rx-test-input"
                            value={testInput}
                            onChange={(e) => setTestInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addTest();
                              }
                            }}
                            placeholder="e.g. CBC, Lipid Profile"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            data-testid="rx-test-add-btn"
                            onClick={addTest}
                            className="rounded-full"
                          >
                            Add
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {rx.tests_recommended.map((t) => (
                            <span
                              key={t}
                              data-testid={`rx-test-tag-${t}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent text-accent-foreground text-xs"
                            >
                              {t}
                              <button type="button" onClick={() => removeTest(t)} className="opacity-60 hover:opacity-100">
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-medium">Advice</Label>
                        <Textarea
                          data-testid="rx-advice"
                          value={rx.advice}
                          onChange={(e) => setRx((r) => ({ ...r, advice: e.target.value }))}
                          placeholder="Lifestyle / general advice"
                          className="mt-1 min-h-[60px]"
                        />
                      </div>

                      <div>
                        <Label className="text-sm font-medium">Follow-up date</Label>
                        <Input
                          data-testid="rx-followup"
                          type="date"
                          value={rx.follow_up_date}
                          onChange={(e) => setRx((r) => ({ ...r, follow_up_date: e.target.value }))}
                          className="mt-1"
                        />
                      </div>

                      <div className="flex gap-2 pt-2 border-t border-border">
                        <Button
                          variant="outline"
                          data-testid="rx-save-draft-btn"
                          onClick={() => savePrescription(false)}
                          disabled={savingRx}
                          className="rounded-full"
                        >
                          <Save className="h-4 w-4 mr-1.5" />
                          {savingRx ? "Saving…" : "Save draft"}
                        </Button>
                        <Button
                          data-testid="rx-finalize-btn"
                          onClick={() => savePrescription(true)}
                          disabled={savingRx}
                          className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
                        >
                          <FileCheck className="h-4 w-4 mr-1.5" />
                          Save & finalize
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <PatientRxView appointmentId={appointmentId} />
                  )}
                </TabsContent>
              </Tabs>
            </aside>
          </div>
        )}
      </main>

      <AlertDialog open={endOpen} onOpenChange={setEndOpen}>
        <AlertDialogContent data-testid="end-consult-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>End this consultation?</AlertDialogTitle>
            <AlertDialogDescription>
              The appointment will be marked completed. The patient will be able to view the
              prescription (if you've saved one).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="end-consult-cancel">Keep open</AlertDialogCancel>
            <AlertDialogAction
              data-testid="end-consult-confirm-btn"
              onClick={completeConsultation}
              disabled={completing}
              className="bg-primary hover:bg-primary/90"
            >
              {completing ? "Completing…" : "End & mark completed"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PatientRxView({ appointmentId }) {
  const [rx, setRx] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/patient/appointments/${appointmentId}/prescription`)
      .then((r) => setRx(r.data && r.data.id ? r.data : null))
      .finally(() => setLoading(false));
  }, [appointmentId]);

  if (loading) return <div className="h-[60vh] bg-muted rounded-2xl animate-pulse" />;
  if (!rx) {
    return (
      <div className="bg-white border border-border rounded-2xl p-6 text-sm text-muted-foreground h-[60vh] flex items-center justify-center text-center">
        Your prescription will appear here once the doctor finalizes it.
      </div>
    );
  }
  return (
    <div className="bg-white border border-border rounded-2xl p-5 space-y-4 h-[60vh] overflow-y-auto">
      <div className="flex items-center justify-between border-b border-border pb-3">
        <div>
          <h3 className="font-bold text-base text-foreground">Medical Prescription</h3>
          <p className="text-xs text-muted-foreground">Issued by consulting doctor</p>
        </div>
        <Button
          size="sm"
          className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5 shadow-sm"
          onClick={() => {
            const token = localStorage.getItem("telemed_token") || localStorage.getItem("healthink_token") || localStorage.getItem("token") || "";
            const backendBase = (api.defaults.baseURL || "").replace(/\/api$/, "");
            window.open(`${backendBase}/api/prescriptions/${rx.id}/pdf${token ? `?token=${encodeURIComponent(token)}` : ''}`, "_blank");
          }}
        >
          <Printer className="h-3.5 w-3.5" /> Print / Download
        </Button>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Diagnosis</div>
        <div className="text-sm mt-0.5 font-medium">{rx.diagnosis || "—"}</div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Medicines</div>
        <ul className="mt-2 space-y-2">
          {(rx.medicines || []).map((m, i) => (
            <li key={i} className="border border-border rounded-xl p-3 text-sm">
              <div className="font-semibold">{m.name}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {[m.dosage, m.frequency, m.duration].filter(Boolean).join(" · ")}
              </div>
              {m.notes && <div className="text-xs mt-1">{m.notes}</div>}
            </li>
          ))}
        </ul>
      </div>
      {rx.tests_recommended?.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Tests</div>
          <div className="text-sm mt-0.5">{rx.tests_recommended.join(", ")}</div>
        </div>
      )}
      {rx.advice && (
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Advice</div>
          <div className="text-sm mt-0.5 whitespace-pre-wrap">{rx.advice}</div>
        </div>
      )}
      {rx.follow_up_date && (
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Follow-up</div>
          <div className="text-sm mt-0.5">{rx.follow_up_date}</div>
        </div>
      )}
    </div>
  );
}
